'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@terra/ui-styled-system';
import { MetaDefault, Skeleton } from '@terra/ui-design-system';
import { ReactFlowProvider } from '@xyflow/react';
import { useTranslations } from 'next-intl';
import type { SignalDto } from '@terra/shared-contracts-signals';
import type {
  ScenarioDto,
  InternalCrownJewelDto,
  AdGraphResponseDto,
} from '@terra/shared-contracts-internal-network';
import { InternalNetworkAdEntityKindSchema } from '@terra/shared-contracts-internal-network';
import { InternalNetworkGraphFlow } from '@terra/ui-graphs';
import {
  buildAttackPathEventGraph,
  buildAttackPathEventTimelineFromHops,
  InternalNetworkAttackPathEventFlow,
} from '@terra/ui-graphs/internal-network';
import { useInternalNetworkGraph } from '../../../hooks/api/useInternalNetworkGraph';
import { resolveScenarioAttackPathCenterNodeId } from './resolve-scenario-attack-path-center-node-id';

const GRAPH_HEIGHT = 400;

const noopSelectNode = (_nodeId: string, _entityKind: string) => undefined;
const noopAction = () => undefined;

function ScenarioAttackPathFlow({
  graph,
  centerNodeId,
  onOpenEntity,
}: {
  graph: AdGraphResponseDto;
  centerNodeId: string;
  onOpenEntity?: (payload: { kind: string; id: string }) => void;
}) {
  const [showNodeLabels, setShowNodeLabels] = useState(true);
  const [showEdgeLabels, setShowEdgeLabels] = useState(true);

  const focusedNodeId =
    graph.nodes.find(
      (node) => node.id === centerNodeId || node.objectId === centerNodeId,
    )?.id ?? centerNodeId;

  useEffect(() => {
    const directNeighborCount = graph.edges.filter(
      (edge) =>
        edge.sourceId === focusedNodeId || edge.targetId === focusedNodeId,
    ).length;

    setShowNodeLabels(directNeighborCount <= 20);
    setShowEdgeLabels(directNeighborCount <= 12);
  }, [graph, focusedNodeId]);

  const handleSelectNode = useCallback(
    (nodeId: string, entityKind: string) => {
      onOpenEntity?.({ kind: entityKind, id: nodeId });
    },
    [onOpenEntity],
  );

  const handleViewDetails = useCallback(
    (nodeId: string) => {
      const node = graph.nodes.find((entry) => entry.id === nodeId);

      if (!node) {
        return;
      }

      const parsedKind = InternalNetworkAdEntityKindSchema.safeParse(node.kind);

      if (!parsedKind.success) {
        return;
      }

      onOpenEntity?.({ kind: parsedKind.data, id: nodeId });
    },
    [graph.nodes, onOpenEntity],
  );

  return (
    <Box h="full" w="full" position="relative">
      <InternalNetworkGraphFlow
        graph={graph}
        layout="radial"
        showNodeLabels={showNodeLabels}
        showEdgeLabels={showEdgeLabels}
        focusedNodeId={focusedNodeId}
        selectedNodeId={null}
        selectedEdgeId={null}
        isExpanding={false}
        viewScope="full"
        centerNodeId={focusedNodeId}
        onSelectNode={onOpenEntity ? handleSelectNode : noopSelectNode}
        onExpandNode={noopAction}
        onViewDetails={onOpenEntity ? handleViewDetails : noopAction}
        onSelectEdge={noopAction}
        onClearSelection={noopAction}
        showMiniMap={false}
        showControls={false}
        onShowNodeLabelsChange={setShowNodeLabels}
        onShowEdgeLabelsChange={setShowEdgeLabels}
      />
    </Box>
  );
}

type ScenarioAttackPathGraphProps = {
  networkId: string;
  scenario: ScenarioDto;
  signal?: SignalDto;
  crownJewels: InternalCrownJewelDto[];
  onOpenEntity?: (payload: { kind: string; id: string }) => void;
};

export function ScenarioAttackPathGraph({
  networkId,
  scenario,
  signal,
  crownJewels,
  onOpenEntity,
}: ScenarioAttackPathGraphProps) {
  const t = useTranslations('internalNetwork.scenarios');
  const timelineGraph = useMemo(() => {
    const steps = scenario.typedTaskPlan?.steps ?? [];

    if (steps.length === 0) {
      return null;
    }

    const timeline = buildAttackPathEventTimelineFromHops({
      id: scenario.id,
      createdAt: scenario.createdAt,
      hops: steps.map((step, hopIndex) => {
        const hasStepSpecificKey = Boolean(step.edgeType || step.command);

        return {
          hopIndex,
          targetHost: step.targetHost ?? step.sourceHost ?? step.action,
          technique: step.technique ?? step.action,
          command: step.command,
          edgeType: step.edgeType,
          category: hasStepSpecificKey
            ? undefined
            : scenario.typedTaskPlan?.signalCategory,
          prerequisiteCredential: step.prerequisites,
        };
      }),
    });

    return buildAttackPathEventGraph(timeline);
  }, [
    scenario.createdAt,
    scenario.id,
    scenario.typedTaskPlan?.steps,
    scenario.typedTaskPlan?.signalCategory,
  ]);

  const centerNodeId = signal
    ? resolveScenarioAttackPathCenterNodeId({
        scenario,
        signal,
        crownJewels,
      })
    : null;

  const { graph, isLoading, isError } = useInternalNetworkGraph({
    networkId,
    queryParams: {
      mode: 'neighborhood',
      centerNodeId: centerNodeId ?? '',
      depth: 1,
    },
    options: { enabled: Boolean(centerNodeId) && !timelineGraph },
  });

  const normalizedGraph = useMemo((): AdGraphResponseDto | undefined => {
    if (!graph) {
      return undefined;
    }

    return {
      ...graph,
      nodes: graph.nodes.map((node) => ({
        ...node,
        isOwned: node.isOwned ?? false,
      })),
    };
  }, [graph]);

  return (
    <Box
      mt={3}
      h={`${GRAPH_HEIGHT}px`}
      w="full"
      borderRadius="lg"
      overflow="hidden"
      borderWidth="1px"
      borderColor="border.neutral.subtle"
    >
      {timelineGraph ? (
        <InternalNetworkAttackPathEventFlow
          nodes={timelineGraph.nodes}
          edges={timelineGraph.edges}
          columns={timelineGraph.columns}
          hideControls
          showTimeAxis={false}
        />
      ) : !centerNodeId ? (
        <Box
          h="full"
          w="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={6}
        >
          <MetaDefault color="fg.muted">{t('attackPathEmpty')}</MetaDefault>
        </Box>
      ) : isLoading ? (
        <Skeleton h="full" w="full" borderRadius="lg" />
      ) : isError || !normalizedGraph || normalizedGraph.nodes.length === 0 ? (
        <Box
          h="full"
          w="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={6}
        >
          <MetaDefault color="fg.muted">{t('attackPathEmpty')}</MetaDefault>
        </Box>
      ) : (
        <ReactFlowProvider key={centerNodeId}>
          <ScenarioAttackPathFlow
            graph={normalizedGraph}
            centerNodeId={centerNodeId}
            onOpenEntity={onOpenEntity}
          />
        </ReactFlowProvider>
      )}
    </Box>
  );
}
